import { Buffer } from "buffer";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { filter, map } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { useToast } from "../../../hooks/useToast";
import { useStyles } from "../styles";
import { ButtonProps } from "@mui/material/Button";
import { CreateCampaignFileData } from "../../../types/campaign";
import { Match } from "../../../components/CreateCampaignFile/Match";
import { Rule } from "../../../components/CreateCampaignFile/Rule";
import { Review } from "../../../components/CreateCampaignFile/Review";
import { File } from "../../../components/CreateCampaignFile/File";
import { FileToBase64 } from "../../../lib/toBase64";
import { useAuth } from "../../../hooks/useAuth";
import { useAws } from "../../../hooks/useAws";
import { useMutation } from "react-query";
import { createHeaders, createImportFile } from "../../../services/imports";
import { CreateHeadersData } from "../../../types/imports";
import { queryClient } from "../../../services/queryClient";

type ImportFileProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({});

export function NewCampaignFile({ onClose, open }: ImportFileProps) {
  const formikRef = useRef<FormikProps<CreateCampaignFileData>>(null);

  const { uploadS3 } = useAws();
  const { user } = useAuth();
  const toast = useToast();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loadingHeader, setLoadingHeader] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);

  const newHeaderFile = useMutation(
    async (data: CreateHeadersData) => {
      const response = await createHeaders(data);
      return response;
    },
    {
      onSuccess: (response) => {
        toast.show("Leitura bem sucedida!", { severity: "success" });
        const columns = map(
          filter(response.cols, (col) => Boolean(col.checked)),
          (col) => ({ label: col.label, column: col.column })
        );
        formikRef.current.setFieldValue(
          "fileName",
          response.attrFile.file_name
        );
        formikRef.current.setFieldValue(
          "fileRows",
          response.attrFile.file_length
        );
        formikRef.current.setFieldValue("fileUrl", response.attrFile.link);
        formikRef.current.setFieldValue("columns", columns);

        setActiveStep((prev) => prev + 1);
      },
      onError: () => {
        toast.show("Não conseguimos identificar o arquivo", {
          severity: "error",
        });
      },
    }
  );

  const newImportFile = useMutation(
    async (data: CreateCampaignFileData) => {
      const response = await createImportFile(data);
      return response;
    },
    {
      onSuccess: (message) => {
        queryClient.invalidateQueries("insights");
        toast.show(message, { severity: "success" });
        handleClose();
      },
      onError: () => {
        toast.show("Não conseguimos fazer a importação", {
          severity: "error",
        });
      },
    }
  );

  const steps = useMemo(() => {
    return ["Arquivo", "Colunas", "Regras", "Resumo"];
  }, []);

  const handleSave = useCallback(
    async (data: CreateCampaignFileData) => {
      try {
        setLoadingFile(true);
        const splitName = data.file.name.split(".");
        const uploadFile = {
          file: Buffer.from(JSON.stringify(data.columns)),
          name: `${data.file.lastModified}-${splitName[0]}`,
          type: "json",
          bucketName: "log-crm",
          folderName: "cabecalho",
          subFolderName: `${user.companyId}/importacao/`,
          distinctName: false,
          extension: "json",
        };
        const url = await uploadS3(uploadFile);
        data.fileHeaderUrl = url;
        await newImportFile.mutateAsync(data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoadingFile(false);
      }
    },
    [user.companyId, newImportFile, uploadS3]
  );

  const handleSaveFile = useCallback(
    async (data: CreateCampaignFileData) => {
      try {
        if (data.file) {
          setLoadingHeader(true);
          const base64 = await FileToBase64(data.file);
          const file = base64.split(";base64,")[1];
          const splitName = data.file.name.split(".");
          const uploadFile = {
            file: Buffer.from(file, "base64"),
            type: data.file.type,
            bucketName: "log-crm",
            folderName: "arquivos",
            name: `${data.file.lastModified}-${splitName[0]}`,
            subFolderName: `${user.companyId}/importacao/`,
            distinctName: false,
            extension: splitName[1],
          };
          const url = await uploadS3(uploadFile);
          const objectRequest = {
            name: uploadFile.name,
            type: uploadFile.extension,
            url,
          };
          await newHeaderFile.mutateAsync(objectRequest);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoadingHeader(false);
      }
    },
    [user.companyId, newHeaderFile, uploadS3]
  );

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => setActiveStep((prev) => prev - 1),
        disabled: activeStep === 0 || loadingHeader || loadingFile,
      },
      {
        disabled: loadingHeader || loadingFile,
        children:
          loadingHeader || loadingFile
            ? "Enviado solicitação..."
            : activeStep === 3
            ? "Importar"
            : "Continuar",
        variant: "contained",
        onClick: () =>
          activeStep === 0
            ? handleSaveFile(formikRef.current.values)
            : activeStep === 3
            ? formikRef.current?.handleSubmit()
            : setActiveStep((prev) => prev + 1),
      },
    ];
  }, [loadingFile, loadingHeader, activeStep, formikRef, handleSaveFile]);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <File />;
      case 1:
        return (
          <Match
            colums={newHeaderFile.data.cols}
            headers={newHeaderFile.data.fileCols}
          />
        );
      case 2:
        return <Rule />;
      case 3:
        return <Review />;
    }
  }, [activeStep, newHeaderFile]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      title="Importar arquivo"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              typeImport: "CAMPANHA",
              rule: "update",
              identificator: { column: "documento" },
            } as CreateCampaignFileData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
